import React, { useEffect, useMemo, useRef } from 'react';
import { Circle, GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { CSpinner } from '@coreui/react-pro';
import { env } from '../../Utils/Environment';
import { FleetCourier } from '../../types/fleet';
import { FleetCourierRoute } from '../../types/deliveryRoute';
import { CourierRouteLines } from './CourierRouteLines';
import { COURIER_STATES } from './fleetUtils';

// Clave de navegador restringida al dominio del portal: REACT_APP_GOOGLE_MAPS_KEY_DEV / _PROD
const MAPS_KEY = env('GOOGLE_MAPS_KEY', '');

interface FleetMapProps {
    couriers: FleetCourier[];
    selectedCourierId: number | null;
    onSelectCourier: (id: number) => void;
    // Recorrido y ruta planeada del domiciliario elegido (si tiene entrega en curso)
    route?: FleetCourierRoute | null;
    height?: number;
}

const toPoint = (lat: string | number | null | undefined, lng: string | number | null | undefined) => {
    const latitude = Number(lat);
    const longitude = Number(lng);
    return lat !== null && lat !== undefined && lat !== '' && !isNaN(latitude) && !isNaN(longitude) ? { lat: latitude, lng: longitude } : null;
};

const FleetMapInner: React.FC<FleetMapProps> = ({ couriers, selectedCourierId, onSelectCourier, route, height = 520 }) => {
    const { isLoaded, loadError } = useJsApiLoader({ id: 'domicilapp-fleet-map', googleMapsApiKey: MAPS_KEY });
    const mapRef = useRef<google.maps.Map | null>(null);
    const fittedRef = useRef(false);

    const located = useMemo(
        () => couriers.map((c) => ({ courier: c, point: toPoint(c.latitude, c.longitude) })).filter((c) => c.point !== null) as { courier: FleetCourier; point: google.maps.LatLngLiteral }[],
        [couriers],
    );

    // Encuadra la flota la primera vez; después respeta el zoom que deje la central
    useEffect(() => {
        if (!isLoaded || !mapRef.current || fittedRef.current || located.length === 0) return;
        const bounds = new google.maps.LatLngBounds();
        located.forEach(({ point }) => bounds.extend(point));
        mapRef.current.fitBounds(bounds, 60);
        if (located.length === 1) mapRef.current.setZoom(15);
        fittedRef.current = true;
    }, [isLoaded, located]);

    // Centra el domiciliario elegido en la lista
    useEffect(() => {
        const selected = located.find((l) => l.courier.id === selectedCourierId);
        if (selected && mapRef.current) mapRef.current.panTo(selected.point);
    }, [selectedCourierId, located]);

    if (loadError) {
        return <div className="p-4 text-center text-body-secondary small">No se pudo cargar Google Maps. Revisa la clave REACT_APP_GOOGLE_MAPS_KEY.</div>;
    }
    if (!isLoaded) {
        return (
            <div className="d-flex align-items-center justify-content-center" style={{ height }}>
                <CSpinner size="sm" className="me-2" /> <span className="text-body-secondary">Cargando mapa...</span>
            </div>
        );
    }

    const selectedDelivery = couriers.find((c) => c.id === selectedCourierId)?.current_delivery;
    const storePoint = selectedDelivery ? toPoint(selectedDelivery.store.latitude, selectedDelivery.store.longitude) : null;
    const destinationPoint = selectedDelivery ? toPoint(selectedDelivery.destination.approx_latitude, selectedDelivery.destination.approx_longitude) : null;

    return (
        <GoogleMap
            mapContainerStyle={{ width: '100%', height, borderRadius: 8 }}
            center={located[0]?.point ?? { lat: 4.711, lng: -74.0721 }}
            zoom={13}
            onLoad={(map) => {
                mapRef.current = map;
            }}
            options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: true, clickableIcons: false }}
        >
            {route && <CourierRouteLines traveled={route.traveled} planned={route.planned} />}

            {located.map(({ courier, point }) => {
                const color = COURIER_STATES[courier.state]?.color ?? '#94A3B8';
                const isSelected = courier.id === selectedCourierId;
                return (
                    <Marker
                        key={courier.id}
                        position={point}
                        title={`${courier.name} · ${COURIER_STATES[courier.state]?.label}`}
                        onClick={() => onSelectCourier(courier.id)}
                        zIndex={isSelected ? 10 : 1}
                        icon={{
                            path: google.maps.SymbolPath.CIRCLE,
                            scale: isSelected ? 11 : 8,
                            fillColor: color,
                            fillOpacity: 1,
                            strokeColor: '#ffffff',
                            strokeWeight: 2,
                        }}
                        label={isSelected ? { text: courier.name.split(' ')[0], color: '#18181b', fontSize: '12px', fontWeight: '600', className: 'fleet-marker-label' } : undefined}
                    />
                );
            })}

            {/* Pedido del domiciliario elegido: comercio y zona aproximada del destino (nunca la dirección exacta) */}
            {storePoint && (
                <Marker
                    position={storePoint}
                    title={selectedDelivery?.store.name}
                    icon={{ path: google.maps.SymbolPath.BACKWARD_CLOSED_ARROW, scale: 6, fillColor: '#FE2445', fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 1 }}
                />
            )}
            {destinationPoint && (
                <Circle center={destinationPoint} radius={600} options={{ fillColor: '#3B82F6', fillOpacity: 0.12, strokeColor: '#3B82F6', strokeOpacity: 0.5, strokeWeight: 1 }} />
            )}
        </GoogleMap>
    );
};

export const FleetMap: React.FC<FleetMapProps> = (props) => {
    if (!MAPS_KEY) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center text-center p-4 text-body-secondary" style={{ height: props.height ?? 520 }}>
                <strong className="text-body mb-1">Mapa no configurado</strong>
                <small>
                    Agrega una clave de Google Maps para navegador en <code>REACT_APP_GOOGLE_MAPS_KEY_DEV</code> / <code>_PROD</code>.
                    Mientras tanto, la lista muestra el estado y la última ubicación de cada domiciliario.
                </small>
            </div>
        );
    }
    return <FleetMapInner {...props} />;
};

export default FleetMap;
